// characterLookApi — read and write a character's saved look WITHOUT loading it.
//
// The launcher's Properties panel edits a background colour on a card. That
// character is not open: `eventGraphState` holds whichever character is
// currently loaded, or nothing at all, so `saveSceneLook` (which reads the live
// graph) cannot serve this. Everything here goes to the server and back.
//
// Colour is editable from the launcher and the camera is not, and that asymmetry
// is deliberate rather than an omission: a colour is a value you can pick with
// no viewport, while a camera pose is only meaningful against what you can see.
// So this patches the ONE backdrop node and leaves any saved camera alone
// (withBackdropPatch) rather than rebuilding the look from a live snapshot,
// which off-screen would have to either drop the camera or invent one.

import { getLatestBlueprint, saveBlueprint } from './assetManagerApi'
import { retryTransient } from './retryTransient.js'
import {
  findSceneLookGroup, withSceneLookGroup, withBackdropPatch, readBackdrop,
} from './sceneLookNodes.js'

/** Read the blueprint, retrying a server that did not answer. Trivially safe:
 *  a GET changes nothing, and the same 502 hit reads as well as writes. */
function readBlueprint(slug) {
  return retryTransient(() => getLatestBlueprint(slug), {
    onRetry: ({ attempt, error }) =>
      console.warn(`[characterLookApi] read attempt ${attempt} failed (${error?.message ?? error}) — retrying`),
  })
}

/** The events graph off a blueprint document, in the shape the app stores. */
function eventsOf(blueprint) {
  const ev = blueprint?.events ?? {}
  return {
    nodes: ev.eventNodes ?? [],
    edges: ev.eventEdges ?? [],
    functions: ev.functions ?? {},
  }
}

/**
 * Store the whole blueprint document with ONLY its event nodes replaced.
 *
 * The WHOLE document, not just `{events}`. The singleton endpoint REPLACES the
 * stored row rather than merging into it, so a payload that omits body and face
 * empties them — the ENG-991 shape (myra 2026-07-01, eyeline 2026-08-13). The
 * server refuses that with a 400 naming the graph it would have destroyed, which
 * is exactly what "the server rejected the write" was: the guard working.
 * Carrying the document through is the fix, not an override — `allow_empty` is
 * the deliberate-wipe flag and a client must never volunteer it.
 *
 * Calls `saveBlueprint` directly rather than `saveBlueprintToDB` so the server's
 * own message survives. That helper returns a bare boolean and console.warns the
 * reason, which left this panel able to say only "rejected" — true, useless, and
 * the reason it took a round of debugging to find out what was wrong.
 */
async function storeEventNodes(slug, blueprint, nodes, events) {
  const body = {
    ...blueprint,
    events: { ...(blueprint?.events ?? {}), eventNodes: nodes, eventEdges: events.edges, functions: events.functions },
  }
  // Retried because the AAM answers this endpoint slowly enough to hit the
  // gateway's upstream timeout (observed: a 502 on POST that succeeded on a
  // manual retry a moment later). The write REPLACES the document, so asking
  // twice lands the same state. A judged refusal — the ENG-991 guard's 400 — is
  // not retried; see retryTransient.js.
  await retryTransient(() => saveBlueprint(slug, body), {
    onRetry: ({ attempt, error }) =>
      console.warn(`[characterLookApi] save attempt ${attempt} failed (${error?.message ?? error}) — retrying`),
  })
}

/**
 * The character's saved backdrop, or null when it has none.
 *
 * Throws on a fetch failure rather than returning null: "this character has no
 * saved look" and "we could not ask" are different facts, and a panel that
 * renders the second as the first invites the user to overwrite something that
 * is actually there. The caller shows the error.
 *
 * @returns {Promise<{backdrop: object|null, events: object}>}
 */
export async function loadCharacterLook(slug) {
  const blueprint = await readBlueprint(slug)
  const events = eventsOf(blueprint)
  return { backdrop: readBackdrop(findSceneLookGroup(events.nodes)), events }
}

/**
 * Merge `patch` into the character's saved backdrop and store it.
 *
 * Re-reads the blueprint immediately before writing rather than trusting a graph
 * fetched when the panel opened: the panel can sit open for a long time, and the
 * write below replaces the whole events graph. A stale read would silently
 * revert any edit made in the editor meanwhile.
 *
 * @returns {Promise<{ok: boolean, message: string, backdrop?: object}>}
 */
export async function saveCharacterBackdrop(slug, patch) {
  if (!slug) return { ok: false, message: 'no character' }

  let blueprint
  try {
    blueprint = await readBlueprint(slug)
  } catch (err) {
    return { ok: false, message: `could not read this character's blueprint — ${err?.message ?? err}` }
  }
  const events = eventsOf(blueprint)

  const group = withBackdropPatch(findSceneLookGroup(events.nodes), patch)
  const nodes = withSceneLookGroup(events.nodes, group)

  try {
    await storeEventNodes(slug, blueprint, nodes, events)
  } catch (err) {
    // The server's own words. It names the graph it refused to destroy, which is
    // the only thing that makes the failure actionable.
    return { ok: false, message: err?.message ?? String(err) }
  }
  return { ok: true, message: 'saved', backdrop: readBackdrop(group) }
}

/**
 * Drop the character's whole saved look.
 *
 * Deliberately removes the GROUP, not just the backdrop node: "clear" on this
 * panel means the character stops carrying a look, and leaving an orphan camera
 * behind would make a character that still opens at someone's saved framing
 * while the panel reports nothing saved.
 */
export async function clearCharacterLook(slug) {
  if (!slug) return { ok: false, message: 'no character' }
  let blueprint
  try {
    blueprint = await readBlueprint(slug)
  } catch (err) {
    return { ok: false, message: `could not read this character's blueprint — ${err?.message ?? err}` }
  }
  const events = eventsOf(blueprint)
  if (!findSceneLookGroup(events.nodes)) return { ok: true, message: 'nothing saved', backdrop: null }

  try {
    await storeEventNodes(slug, blueprint, withSceneLookGroup(events.nodes, null), events)
  } catch (err) {
    return { ok: false, message: err?.message ?? String(err) }
  }
  return { ok: true, message: 'saved look cleared', backdrop: null }
}
